interface Weight {
  freq: number;
  weight: number;
}

/**
 * Based on found frequency analysis of letters in english
 * ie. https://github.com/azaralili/cryptography
 *
 * weights are computed on the frequencies:
 * if freq <= 1, then 1
 * if freq > 1, then round(log2(freq))
 */
const WEIGHTS: Record<string, Weight> = {
  a: { freq: 8.17, weight: 3 },
  b: { freq: 1.49, weight: 1 },
  c: { freq: 2.78, weight: 1 },
  d: { freq: 4.25, weight: 2 },
  e: { freq: 12.7, weight: 4 },
  f: { freq: 2.23, weight: 1 },
  g: { freq: 2.02, weight: 1 },
  h: { freq: 6.09, weight: 3 },
  i: { freq: 6.97, weight: 3 },
  j: { freq: 0.15, weight: 1 },
  k: { freq: 0.77, weight: 1 },
  l: { freq: 4.03, weight: 2 },
  m: { freq: 2.41, weight: 1 },
  n: { freq: 6.75, weight: 3 },
  o: { freq: 7.51, weight: 3 },
  p: { freq: 1.93, weight: 1 },
  q: { freq: 0.1, weight: 1 },
  r: { freq: 5.99, weight: 3 },
  s: { freq: 6.33, weight: 3 },
  t: { freq: 9.06, weight: 3 },
  u: { freq: 2.76, weight: 1 },
  v: { freq: 0.98, weight: 1 },
  w: { freq: 2.36, weight: 1 },
  x: { freq: 0.15, weight: 1 },
  y: { freq: 1.97, weight: 1 },
  z: { freq: 0.07, weight: 1 },
};

/**
 * Mappings of lower & upper cases english alphabet
 * to more complex variations of the letter
 */
const VARIATIONS: Record<string, string[]> = {
  a: ['å', 'ą', 'æ', 'ǻ'],
  b: ['ḅ', 'β', 'ɓ'],
  c: ['ç', 'c̃', 'ç̣'],
  d: ['ḍ', 'ɖ', 'ð'],
  e: ['é', 'ȩ̣', 'ɛ', 'e͆̋'],
  f: ['ϝ', 'f̧̣', 'ſ'],
  g: ['ḡ', 'g̥', 'ģ̣'],
  h: ['ḥ', 'h̃', 'h̶'],
  i: ['î', 'į', 'ḭ', 'ɨ'],
  j: ['ĵ', 'ʝ', 'ȷ̣'],
  k: ['ḳ', 'ǩ', 'k̸̊'],
  l: ['ḽ', 'ł', 'λ'],
  m: ['ṃ', 'μ', 'ɱ'],
  n: ['ň', 'ŋ', 'ȵ', 'ṇ'],
  o: ['ṓ', 'ø', 'ǒ', 'ɵ'],
  p: ['ṗ', 'p̃', 'p̸̊'],
  q: ['q̶̣', 'ʠ', 'q́'],
  r: ['ṛ', 'ř', 'ϱ'],
  s: ['ṡ', 'š', 'ş', 'ʂ'],
  t: ['ṭ', 'ƫ', 'τ', 't́'],
  u: ['ṵ', 'û', 'ʉ', 'ú̄'],
  v: ['ṽ', 'v̧', 'v́'],
  w: ['ẁ', 'ẉ', 'ẘ'],
  x: ['ẋ', 'ᶍ', 'x̀'],
  y: ['ẏ', 'ƴ', 'ŷ', 'ɣ'],
  z: ['ẓ', 'ž', 'ʑ'],
  A: ['Å', 'Ą', 'Æ', 'Ǻ'],
  B: ['Ḃ', 'Ḅ', 'Ɓ'],
  C: ['Ḉ', 'C̃', 'Ç̣'],
  D: ['Ḍ', 'Ɖ', 'D̸̊'],
  E: ['É', 'Ȩ̣', 'Ɛ', 'E͆̋'],
  F: ['Ƒ', 'Ḟ', 'F̧̣'],
  G: ['Ḡ', 'G̥', 'Ģ̣'],
  H: ['Ḥ', 'H̃', 'H̶'],
  I: ['Ḭ', 'Î', 'Į', 'Ɨ'],
  J: ['Ĵ', 'ʝ', 'ȷ̣'],
  K: ['Ḳ', 'Ǩ', 'K̸̊'],
  L: ['Ḻ', 'Ł', 'Λ'],
  M: ['Ṁ', 'Ṃ', 'ɱ'],
  N: ['Ň', 'Ŋ', 'ȵ', 'Ṇ'],
  O: ['Ṏ', 'Ṓ', 'Ø', 'Ɵ'],
  P: ['Ṕ', 'P̃', 'P̸̊'],
  Q: ['Q̶̣', 'Q́', 'ʠ'],
  R: ['Ṛ', 'Ř', 'R̸̊'],
  S: ['Ṣ', 'Ṡ', 'Š', 'Ş'],
  T: ['Ṭ', 'Ƭ', 'T́'],
  U: ['Ṵ', 'Û', 'Ʉ', 'Ú̄'],
  V: ['Ṿ', 'Ṽ', 'V́'],
  W: ['Ẁ', 'Ẉ', 'Ŵ'],
  X: ['Ẋ', 'X̨', 'X̀'],
  Y: ['Ŷ', 'Ƴ', 'Γ', 'Ẏ'],
  Z: ['Ż', 'Ž', 'Ẓ'],
};

const pseudoLocalizeChar = (char: string, nextChar: string) => {
  const variation = VARIATIONS[char];
  if (!variation) {
    return char;
  }
  const weight = WEIGHTS[char.toLowerCase()];
  if (char === nextChar && weight.weight > 1) {
    return undefined;
  }
  const length = Math.min(variation.length, weight.weight);
  const result = Array.from({ length }, (_, index) => variation[index % length]);
  return result.join('');
};

const pseudoLocalize = (text: string) => {
  if (!text) {
    return '';
  }
  return (
    text
      // eslint-disable-next-line unicorn/prefer-spread
      .split('')
      .map((value, key, array) => pseudoLocalizeChar(value, array[key + 1]))
      .filter((char) => char !== undefined)
      .join('')
  );
};

export default pseudoLocalize;
